import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'motion/react';
import {
  Plus, Bot, Grid3X3, Zap, Settings as SettingsIcon,
  MoreHorizontal, ChevronRight, Workflow,
  Home} from 'lucide-react';
import type { Conversation } from '@/types/api';
import { ConversationListItem } from './ConversationListItem';
// v3.7.5: skeleton components (props complete, full render wiring in next version)
import { ConversationList } from './sidebar/ConversationList';
import { ProjectTree } from './sidebar/ProjectTree';
import { SidebarNav } from './sidebar/SidebarNav';
import { SidebarHeader } from './sidebar/SidebarHeader';
import * as projectService from '@/services/projectService';
import { searchConversations, patchConversation, createConversation, deleteConversation } from '@/services/conversationService';
import { apiFetch } from '@/services/apiClient';

interface ChatSidebarProps {
  user?: any;
  t?: any;
  lang: 'en' | 'zh';
  activeConversationId: string | null;
  activeTab?: string;
  onSelectConversation: (id: string | null) => void;
  onNewChat?: () => void;
  onNavigate: (tab: string) => void;
  onDeleteConversation?: (id: string) => void;
  onQuickSkill?: (skillName: string) => void;
  onReturnToDesktop?: () => void;
}

interface NavSection {
  id: string;
  icon: React.ReactNode;
  labelZh: string;
  labelEn: string;
  tab?: string;
}

const PROJECT_COLORS = ['#8B7CFF', '#C9A96A', '#6EA8FE', '#34D399', '#FBBF24', '#F87171'];

type FilterMode = 'all' | 'starred' | 'pinned';

export function ChatSidebar({
  user, t, lang, activeConversationId, activeTab,
  onSelectConversation, onNewChat, onNavigate, onDeleteConversation, onQuickSkill, onReturnToDesktop}: ChatSidebarProps) {
  const isZh = lang === 'zh';
  const portalRoot = typeof document !== 'undefined' ? document.body : null;
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [projects, setProjects] = useState<projectService.Project[]>([]);
  const [plans, setPlans] = useState<projectService.ProjectPlan[]>([]);
  const [skills, setSkills] = useState<Array<{ name: string; description: string; enabled: boolean }>>([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  //  (module A)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchConvIds, setSearchConvIds] = useState<Set<string>>(new Set());
  const [searching, setSearching] = useState(false);

  // Filter (module B)
  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  // Selection / batch (module C)
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchMoveOpen, setBatchMoveOpen] = useState(false);

  // Project space block state
  const [projectsExpanded, setProjectsExpanded] = useState(true);
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [projectConvs, setProjectConvs] = useState<Record<string, projectService.ProjectConversation[]>>({});
  const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  // Tasks (global plans) node (module E)
  const [plansExpanded, setPlansExpanded] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [newPlanTitle, setNewPlanTitle] = useState('');
  const [creatingPlan, setCreatingPlan] = useState(false);

  // Skills quick launch (module G)
  const [skillsExpanded, setSkillsExpanded] = useState(false);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/conversations?limit=50');
      if (res.ok) {
        const data = await res.json();
        setConversations(Array.isArray(data) ? data : (data.conversations || []));
      }
    } catch (err) {
      console.warn('[ChatSidebar] Failed to fetch conversations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const list = await projectService.listProjects();
      setProjects(list);
    } catch (err) {
      console.warn('[ChatSidebar] Failed to fetch projects:', err);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    setLoadingPlans(true);
    try {
      const list = await projectService.listProjectPlans();
      setPlans(list);
    } catch (err) {
      console.error('[ChatSidebar] Failed to load plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  const fetchSkills = useCallback(async () => {
    try {
      const res = await apiFetch('/api/skills');
      if (res.ok) {
        const data = await res.json();
        setSkills((data.skills || []).filter((s: any) => s.enabled));
      }
    } catch (err) {
      console.warn('[ChatSidebar] Failed to fetch skills:', err);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    fetchProjects();
    fetchPlans();
    fetchSkills();
    const handler = () => { fetchConversations(); fetchProjects(); fetchPlans(); };
    window.addEventListener('xf:refresh-conversations', handler);
    return () => window.removeEventListener('xf:refresh-conversations', handler);
  }, [fetchConversations, fetchProjects, fetchPlans, fetchSkills]);

  // Module A: debounced search (with cancellation to prevent stale results overwriting newer ones)
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) { setSearchConvIds(new Set()); setSearching(false); return; }
    setSearching(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await searchConversations(q, 200);
        if (cancelled) return;
        setSearchConvIds(new Set(results.map((r) => r.conversationId)));
      } catch (err) {
        if (!cancelled) console.warn('[ChatSidebar] Search failed:', err);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchQuery]);

  const projectMap = useMemo(() => {
    const m: Record<string, projectService.Project> = {};
    for (const p of projects) m[p.id] = p;
    return m;
  }, [projects]);

  // Derived list: search + filter + sort (pinned first)
  const baseList = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = [...conversations];
    if (q) {
      list = list.filter((c) => searchConvIds.has(c.id) || (c.title || '').toLowerCase().includes(q) || (c.preview || c.lastMessage || '').toLowerCase().includes(q));
    }
    if (filterMode === 'starred') list = list.filter((c) => c.starred);
    if (filterMode === 'pinned') list = list.filter((c) => c.pinned);
    list.sort((a, b) => {
      if (!!b.pinned !== !!a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
      return new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime();
    });
    return list;
  }, [conversations, searchQuery, searchConvIds, filterMode]);

  const pinnedList = useMemo(() => baseList.filter((c) => c.pinned), [baseList]);
  const restList = useMemo(() => baseList.filter((c) => !c.pinned), [baseList]);

  // Module B: group by time
  const timeGroups = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 86400000;
    const dow = now.getDay() === 0 ? 6 : now.getDay() - 1;
    const startOfWeek = startOfToday - dow * 86400000;
    const groups: { key: string; label: string; items: Conversation[] }[] = [
      { key: 'today', label: isZh ? '今天' : 'Today', items: [] },
      { key: 'yesterday', label: isZh ? '昨天' : 'Yesterday', items: [] },
      { key: 'week', label: isZh ? '本周' : 'This Week', items: [] },
      { key: 'earlier', label: isZh ? '更早' : 'Earlier', items: [] },
    ];
    for (const c of restList) {
      const t = new Date(c.lastActiveAt).getTime();
      if (t >= startOfToday) groups[0].items.push(c);
      else if (t >= startOfYesterday) groups[1].items.push(c);
      else if (t >= startOfWeek) groups[2].items.push(c);
      else groups[3].items.push(c);
    }
    return groups.filter((g) => g.items.length > 0);
  }, [restList, isZh]);

  const runningPlanCount = useMemo(
    () => plans.filter((p) => p.status === 'active' || p.status === 'running').length,
    [plans],
  );

  const formatRelativeTime = (iso: string) => {
    const now = Date.now();
    const then = new Date(iso).getTime();
    const diff = now - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return isZh ? '刚刚' : 'just now';
    if (mins < 60) return isZh ? `${mins}分钟前` : `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return isZh ? `${hours}小时前` : `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return isZh ? `${days}天前` : `${days}d ago`;
    return new Date(iso).toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US');
  };

  const handleAssignProject = useCallback(async (conversationId: string, projectId: string | null) => {
    try {
      await projectService.bindConversationToProject(conversationId, projectId);
      setConversations((prev) => prev.map((c) => c.id === conversationId ? { ...c, projectId: projectId || undefined } as Conversation : c));
      window.dispatchEvent(new Event('xf:refresh-conversations'));
    } catch (err) {
      console.error('[ChatSidebar] Assign project failed:', err);
    }
  }, []);

  const handleDelete = async (id: string) => {
    if (onDeleteConversation) await onDeleteConversation(id);
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (activeConversationId === id) onSelectConversation(null);
  };

  const handleNewChat = () => {
    if (onNewChat) onNewChat();
    else onSelectConversation(null);
  };

  const handleToggleStar = async (id: string) => {
    const conv = conversations.find((c) => c.id === id);
    const next = !conv?.starred;
    setConversations((prev) => prev.map((c) => c.id === id ? { ...c, starred: next } : c));
    try { await patchConversation(id, { starred: next }); } catch (err) { console.error('[ChatSidebar] Toggle star failed:', err); }
  };

  const handleTogglePin = async (id: string) => {
    const conv = conversations.find((c) => c.id === id);
    const next = !conv?.pinned;
    setConversations((prev) => prev.map((c) => c.id === id ? { ...c, pinned: next } : c));
    try { await patchConversation(id, { pinned: next }); } catch (err) { console.error('[ChatSidebar] Toggle pin failed:', err); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBatchDelete = async () => {
    const ids = [...selectedIds];
    const failedIds: string[] = [];
    for (const id of ids) {
      try { await deleteConversation(id); } catch (err) { failedIds.push(id); console.error('[ChatSidebar] Delete failed:', err); }
    }
    // P2-4 fix: only remove successfully deleted conversations from UI
    setConversations((prev) => prev.filter((c) => selectedIds.has(c.id) && !failedIds.includes(c.id)));
    if (activeConversationId && selectedIds.has(activeConversationId) && !failedIds.includes(activeConversationId)) onSelectConversation(null);
    setSelectedIds(new Set());
    setSelectionMode(false);
  };

  const handleBatchMove = async (projectId: string | null) => {
    const ids = [...selectedIds];
    for (const id of ids) {
      try { await projectService.bindConversationToProject(id, projectId); } catch (err) { console.error('[ChatSidebar] Batch move failed:', err); }
    }
    setConversations((prev) => prev.map((c) => selectedIds.has(c.id) ? { ...c, projectId: projectId || undefined } : c));
    setSelectedIds(new Set());
    setSelectionMode(false);
    setBatchMoveOpen(false);
    window.dispatchEvent(new Event('xf:refresh-conversations'));
  };

  const navSections: NavSection[] = [
    { id: 'assistant', icon: <Bot size={16} />, labelZh: '助理', labelEn: 'Assistant' },
    { id: 'ecosystem', icon: <Grid3X3 size={16} />, labelZh: '项目空间', labelEn: 'Projects', tab: 'ecosystem' },
    { id: 'skills', icon: <Zap size={16} />, labelZh: '技能中心', labelEn: 'Skills', tab: 'skills' },
    { id: 'automation', icon: <Workflow size={16} />, labelZh: '自动化', labelEn: 'Automation', tab: 'automation' },
    { id: 'more', icon: <MoreHorizontal size={16} />, labelZh: '更多', labelEn: 'More', tab: 'more' },
  ];

  // ---- Collapsed rail (module F: badges) ----
  if (collapsed) {
    const RailButton = ({ icon, onClick, title, badge }: { icon: React.ReactNode; onClick: () => void; title: string; badge?: number }) => (
      <button
        onClick={onClick}
        title={title}
        className="relative w-9 h-9 flex items-center justify-center rounded-xl text-white/40 hover:text-celestial-saturn hover:bg-white/[0.06] transition-all duration-200"
      >
        {icon}
        {badge ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 flex items-center justify-center text-[9px] font-bold rounded-full bg-celestial-saturn text-black">
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </button>
    );
    return (
      <motion.div
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="w-[52px] flex-shrink-0 h-full flex flex-col items-center gap-1.5 border-r border-white/[0.06] bg-[#060810]/80 backdrop-blur-xl py-3"
      >
        <button onClick={() => setCollapsed(false)} title={isZh ? '展开侧边栏' : 'Expand'} className="w-9 h-9 flex items-center justify-center rounded-xl text-white/40 hover:text-celestial-saturn hover:bg-white/[0.06] transition-all">
          <ChevronRight size={16} />
        </button>
        <RailButton icon={<Home size={16} />} onClick={() => onReturnToDesktop?.()} title={isZh ? '返回桌面' : 'Return to Desktop'} />
        <RailButton icon={<Plus size={16} />} onClick={handleNewChat} title={isZh ? '新建对话' : 'New Chat'} />
        <div className="w-6 h-px bg-white/10 my-1" />
        {navSections.map((item) => {
          const badge = item.id === 'assistant' ? conversations.length : item.id === 'automation' ? runningPlanCount : undefined;
          return (
            <RailButton key={item.id} icon={item.icon} onClick={() => onNavigate(item.tab || item.id)} title={isZh ? item.labelZh : item.labelEn} badge={badge} />
          );
        })}
        <div className="flex-1" />
        <RailButton icon={<SettingsIcon size={16} />} onClick={() => onNavigate('settings')} title={isZh ? '设置' : 'Settings'} />
      </motion.div>
    );
  }

  // ---- Project expand / create / plans ----
  const toggleProject = async (pid: string) => {
    if (expandedProjectId === pid) { setExpandedProjectId(null); return; }
    setExpandedProjectId(pid);
    if (!projectConvs[pid]) {
      setLoadingProjectId(pid);
      try {
        const list = await projectService.listProjectConversations(pid);
        setProjectConvs((prev) => ({ ...prev, [pid]: list }));
      } catch (err) {
        console.error('[ChatSidebar] Failed to load project conversations:', err);
      } finally {
        setLoadingProjectId(null);
      }
    }
  };

  const handleCreateProject = async () => {
    const name = newName.trim();
    if (!name) return;
    const color = PROJECT_COLORS[projects.length % PROJECT_COLORS.length];
    try {
      await projectService.createProject(name, '', color);
      setNewName('');
      setCreating(false);
      await fetchProjects();
    } catch (err) {
      console.error('[ChatSidebar] Create project failed:', err);
    }
  };

  const handleRenameProject = async (pid: string) => {
    const name = editingName.trim();
    if (!name) { setEditingProjectId(null); return; }
    try {
      await projectService.updateProject(pid, { name });
      await fetchProjects();
    } catch (err) {
      console.error('[ChatSidebar] Rename project failed:', err);
    }
    setEditingProjectId(null);
  };

  const handleDeleteProject = async (pid: string) => {
    try {
      await projectService.deleteProject(pid);
      await fetchProjects();
    } catch (err) {
      console.error('[ChatSidebar] Delete project failed:', err);
    }
  };

  const handleNewConversationInProject = async (pid: string) => {
    try {
      const conv = await createConversation({ title: isZh ? '新对话' : 'New Chat', projectId: pid });
      onSelectConversation(conv.id);
    } catch (err) {
      console.error('[ChatSidebar] Create in-project conversation failed:', err);
    }
  };

  const handleCreatePlan = async () => {
    const title = newPlanTitle.trim();
    if (!title) return;
    try {
      await projectService.createPlan(title);
      setNewPlanTitle('');
      setCreatingPlan(false);
      await fetchPlans();
    } catch (err) {
      console.error('[ChatSidebar] Create plan failed:', err);
    }
  };

  const handleDeletePlan = async (id: string) => {
    try {
      await projectService.deletePlan(id);
      await fetchPlans();
    } catch (err) {
      console.error('[ChatSidebar] Delete plan failed:', err);
    }
  };

  const planProgress = (pl: projectService.ProjectPlan) => {
    const steps = pl.steps || [];
    if (steps.length === 0) return pl.status === 'done' ? 100 : 0;
    const done = steps.filter((s) => s.status === 'done').length;
    return Math.round((done / steps.length) * 100);
  };

  const planStatusColor = (status: string) => {
    if (status === 'done' || status === 'completed') return '#34D399';
    if (status === 'active' || status === 'running') return '#FBBF24';
    if (status === 'error' || status === 'cancelled') return '#F87171';
    return '#8B7CFF';
  };

  const renderConvItem = (conv: Conversation) => (
    <ConversationListItem
      key={conv.id}
      conversation={conv}
      isActive={activeConversationId === conv.id}
      onClick={() => onSelectConversation(conv.id)}
      onDelete={() => handleDelete(conv.id)}
      formatTime={formatRelativeTime}
      isZh={isZh}
      projectName={conv.projectId ? projectMap[conv.projectId]?.name : undefined}
      projectColor={conv.projectId ? projectMap[conv.projectId]?.color : undefined}
      projects={projects}
      onAssignProject={handleAssignProject}
      starred={conv.starred}
      pinned={conv.pinned}
      onToggleStar={handleToggleStar}
      onTogglePin={handleTogglePin}
      selectionMode={selectionMode}
      selected={selectedIds.has(conv.id)}
      onToggleSelect={toggleSelect}
    />
  );

  return (
    <motion.div
      initial={{ x: -40, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      className="relative z-[30] w-[260px] flex-shrink-0 h-full flex flex-col border-r border-white/[0.06] bg-[#060810]/80 backdrop-blur-xl"
    >
      {/* v3.7.6: SidebarHeader (extracted) */}
      <SidebarHeader
        collapsed={collapsed} setCollapsed={setCollapsed}
        searchQuery={searchQuery} setSearchQuery={setSearchQuery} searching={searching}
        onNewChat={handleNewChat} onReturnToDesktop={onReturnToDesktop}
        lang={lang} isZh={isZh}
        selectionMode={selectionMode} setSelectionMode={setSelectionMode} setSelectedIds={setSelectedIds}
      />

      {/* v3.7.6: SidebarNav (extracted) */}
      <SidebarNav
        activeTab={activeTab} onNavigate={onNavigate} isZh={isZh}
        collapsed={collapsed} runningPlanCount={runningPlanCount} conversationsCount={conversations.length}
      />

      {/* Divider */}
      <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

      {/* v3.7.8: ConversationList (extracted) */}
      <ConversationList
        conversations={conversations} filterMode={filterMode} setFilterMode={setFilterMode}
        activeConversationId={activeConversationId} onSelectConversation={onSelectConversation}
        onDelete={handleDelete} onToggleStar={handleToggleStar} onTogglePin={handleTogglePin}
        selectionMode={selectionMode} selectedIds={selectedIds}               setSelectionMode={setSelectionMode}
              setSelectedIds={setSelectedIds}
toggleSelect={toggleSelect}
        handleBatchDelete={handleBatchDelete} handleBatchMove={handleBatchMove}
        batchMoveOpen={batchMoveOpen} setBatchMoveOpen={setBatchMoveOpen}
        projects={projects} lang={lang} isZh={isZh}
        timeGroups={timeGroups} pinnedList={pinnedList} loading={loading}
        searchQuery={searchQuery} searchConvIds={searchConvIds}
        projectMap={projectMap} baseList={baseList}
        portalRoot={portalRoot} handleAssignProject={handleAssignProject}
      />
      {/* v3.7.9: ProjectTree (extracted) */}
      <ProjectTree
        projects={projects} plans={plans} skills={skills}
        projectsExpanded={projectsExpanded} setProjectsExpanded={setProjectsExpanded}
        expandedProjectId={expandedProjectId} toggleProject={toggleProject}
        plansExpanded={plansExpanded} setPlansExpanded={setPlansExpanded}
        skillsExpanded={skillsExpanded} setSkillsExpanded={setSkillsExpanded}
        onCreateProject={handleCreateProject} onRenameProject={handleRenameProject}
        onDeleteProject={handleDeleteProject} onCreatePlan={handleCreatePlan}
        onDeletePlan={handleDeletePlan} onNewConversationInProject={handleNewConversationInProject}
        onNavigate={onNavigate} onQuickSkill={onQuickSkill}
        lang={lang} isZh={isZh}
        creating={creating} setCreating={setCreating} newName={newName} setNewName={setNewName}
        editingProjectId={editingProjectId} setEditingProjectId={setEditingProjectId}
        editingName={editingName} setEditingName={setEditingName}
        projectConvs={projectConvs} loadingProjectId={loadingProjectId}
        loadingPlans={loadingPlans} creatingPlan={creatingPlan} setCreatingPlan={setCreatingPlan}
        newPlanTitle={newPlanTitle} setNewPlanTitle={setNewPlanTitle}
        activeConversationId={activeConversationId} onSelectConversation={onSelectConversation}
        activeTab={activeTab} fetchPlans={fetchPlans} fetchSkills={fetchSkills}
      />

      {/* Settings at bottom */}
      <div className="px-3 pb-4 pt-2 border-t border-white/[0.06]">
        <button
          onClick={() => onNavigate('settings')}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
            activeTab === 'settings' ? 'bg-white/[0.08] text-celestial-saturn' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.04]'
          }`}
        >
          <SettingsIcon size={16} className={activeTab === 'settings' ? 'text-celestial-saturn' : 'text-white/30'} />
          <span>{isZh ? '设置' : 'Settings'}</span>
        </button>
      </div>
    </motion.div>
  );
}
